import React from 'react';
import Modal from 'components/UI/DialogModal';
import {
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography
} from '@mui/material';
import MuiButton from 'components/UI/MuiButton';
import { useTranslation } from 'react-i18next';
import EmptyContent from 'components/EmptyContent';
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";

interface Workshop {
  workshopId: string;
  workshopTitle: string;
  workshopTitleAr: string;
  companyName: string;
  companyNameAr: string;
  trainerName: string;
  trainerNameAr: string;
  fromDate: string;
  toDate: string;
  status: number;
}

interface Props {
  open: boolean;
  loading: boolean;
  workshops: Workshop[];
  assessmentTitle: string;
  onClose: () => void;
}

const ViewAssessmentWorkshops: React.FC<Props> = ({ open, loading, workshops, assessmentTitle, onClose }) => {
  const { t, i18n } = useTranslation();

  const handleViewWorkshop = (workshopId: string) => {
    window.open(`/workshops/${workshopId}`, '_blank');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t('T_workshops_using_assessment')}: ${assessmentTitle}`}
      maxWidth="lg"
      children={
        <Stack spacing={2} p={2}>
          {loading ? (
            <Stack alignItems="center" p={4}>
              <CircularProgress />
            </Stack>
          ) : workshops.length === 0 ? (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_workshops_using_assessment")}
              message={t("T_no_workshops_using_assessment_message")}
            />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('T_company')}</TableCell>
                    <TableCell>{t('T_workshop_title')}</TableCell>
                    <TableCell>{t('T_trainer')}</TableCell>
                    <TableCell>{t('T_from_date')}</TableCell>
                    <TableCell>{t('T_to_date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workshops.map((workshop) => (
                    <TableRow
                      key={workshop.workshopId}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => handleViewWorkshop(workshop.workshopId)}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {i18n.language === 'en' ? workshop.companyName : workshop.companyNameAr || workshop.companyName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {i18n.language === 'en' ? workshop.workshopTitle : workshop.workshopTitleAr || workshop.workshopTitle}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {i18n.language === 'en' ? (workshop.trainerName || '-') : (workshop.trainerNameAr || workshop.trainerName || '-')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(workshop.fromDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(workshop.toDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      }
      actions={
        <Grid container alignItems={'center'} justifyContent={'flex-end'} px={1}>
          <MuiButton
            variant="contained"
            sx={{ bgcolor: 'primary.main', color: 'white' }}
            size='large'
            onClick={onClose}
          >
            {t('T_close')}
          </MuiButton>
        </Grid>
      }
    />
  );
};

export default ViewAssessmentWorkshops;

